import { useCallback, useEffect, useState } from 'react';
import { useCombobox, UseComboboxStateChange } from 'downshift';
import useSlingApi from '@sdk/api/useSlingApi';
import useDebounce from '@sdk/utils/hooks/useDebounce';

import type { GeoAddress } from './geoAddressField.types';
import { extractAddressString } from './geoAddressField.utils';

type Props = {
  onChange: (value: GeoAddress) => void;
  value: string;
  onIsOpenChange?: (changes: UseComboboxStateChange<GeoAddress>) => void;
};

export const useGeoAddressField = ({ onChange, value, onIsOpenChange }: Props) => {
  const api = useSlingApi();
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [searchQuery, setSearchQuery] = useState('');
  const [addresses, setAddresses] = useState([]);
  const debouncedSearch = useDebounce(searchQuery, 500);

  const search = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const { data } = await api.geo.getGeocodeAddress(debouncedSearch);
      const mappedAddresses = data.map((item) => ({
        address: item.location,
        coordinates: item.coordinates,
      }));

      setAddresses(mappedAddresses);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [api.geo, debouncedSearch]);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 2) {
      search();
    } else {
      setAddresses([]);
    }
  }, [search, debouncedSearch]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: addresses,
    itemToString: extractAddressString,
    onStateChange: (changes) => {
      switch (changes.type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if ('selectedItem' in changes) {
            setInputValue(changes.inputValue);
            onChange(changes.selectedItem);
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setSearchQuery(changes.inputValue);
          setInputValue(changes.inputValue);
          onChange({ address: changes.inputValue });
          break;
      }
    },
    onIsOpenChange,
  });

  return {
    getComboboxProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    inputValue,
    isLoading,
    highlightedIndex,
    isOpen,
    addresses,
  };
};
